import type { ReactNode } from "react";
import { useState } from "react";
import type { LoadType, ShipmentDetail } from "api";
import cn from "classnames";
import { carrierLabel } from "components/Shipments/shipments.model";
import { Modal } from "components/ui/Modal";
import type { SelectOption } from "components/ui/Select";
import { Select } from "components/ui/Select";
import type { CargoForm, CargoLineForm, ContainerForm } from "./cargo.model";
import {
  billLabel,
  blankCargoLine,
  blankContainer,
  CARGO_GROUPS,
  CONTAINER_TYPES,
  containerHasData,
  DG_OPTIONS,
  gridCount,
  hasCargoGroup,
  lineHasData,
  PACKAGE_TYPES,
  sanitizeDecimal,
  sanitizeInteger,
  VOLUME_UNITS,
  WEIGHT_UNITS,
  withCurrent,
} from "./cargo.model";
import { AddRowIcon, AttachIcon, NotesIcon, RemoveRowIcon } from "./docs.icons";
import type { CargoFormState } from "./useCargoForm";

type CargoTabProps = {
  shipment: ShipmentDetail;
  cargo: CargoFormState;
  /** Documents per saved container, for the badge on its attach link. */
  documentCounts: Record<number, number>;
  onOpenDocuments: (containerId: number) => void;
  onOpenNote: (containerKey: string) => void;
};

const LOADS: { value: LoadType; label: string; hint: string }[] = [
  { value: "FCL", label: "FCL", hint: "Full container load" },
  { value: "LCL", label: "LCL", hint: "Less container load" },
];

const DG_SELECT: SelectOption[] = DG_OPTIONS.map(({ value, label, hint, group }) => ({
  value,
  label,
  hint,
  group: group || undefined,
}));

const options = (values: string[]): SelectOption[] => values.map((value) => ({ value, label: value }));

/** Which confirmation is up: the design asks before each of these loses data. */
type Confirm = "container" | "line" | "lcl" | null;

type FieldProps = {
  label: string;
  error?: string;
  children: ReactNode;
};

const Field = ({ label, error, children }: FieldProps) => (
  <label className={cn("shipp__f", { "is-bad": error !== undefined })}>
    <span>{label}</span>
    {children}
    {error !== undefined && <em className="shipp__err">{error}</em>}
  </label>
);

/**
 * The Cargo tab: load type, the shipment's identity and product fields, and
 * the container grid — FCL columns, or LCL cargo lines inside the container.
 * It edits the page's form; the header's Save sends it.
 */
export const CargoTab = ({ shipment, cargo, documentCounts, onOpenDocuments, onOpenNote }: CargoTabProps) => {
  const { form, update, errors } = cargo;
  const [confirm, setConfirm] = useState<Confirm>(null);
  const lcl = form.load_type === "LCL";
  const bill = billLabel(shipment);
  const lastContainer = form.containers[form.containers.length - 1];

  const setField = (field: keyof Omit<CargoForm, "containers">, value: string) =>
    update((current) => ({ ...current, [field]: value }));

  const setContainer = (key: string, change: Partial<ContainerForm>) =>
    update((current) => ({
      ...current,
      containers: current.containers.map((container) =>
        container.key === key ? { ...container, ...change } : container
      ),
    }));

  const setLine = (containerKey: string, lineKey: string, change: Partial<CargoLineForm>) =>
    update((current) => ({
      ...current,
      containers: current.containers.map((container) =>
        container.key === containerKey
          ? {
              ...container,
              cargo_lines: container.cargo_lines.map((line) => (line.key === lineKey ? { ...line, ...change } : line)),
            }
          : container
      ),
    }));

  /** LCL shares one container, so switching keeps only the first row — asked first when there are more. */
  const switchTo = (load: LoadType) => {
    if (load === form.load_type) return;
    if (load === "LCL" && form.containers.length > 1) {
      setConfirm("lcl");
      return;
    }

    setField("load_type", load);
  };

  const commitLcl = () =>
    update((current) => ({ ...current, load_type: "LCL", containers: current.containers.slice(0, 1) }));

  const addContainer = () =>
    update((current) => ({
      ...current,
      containers: [...current.containers, blankContainer(lastContainer?.container_type || undefined)],
    }));

  const removeContainer = () => update((current) => ({ ...current, containers: current.containers.slice(0, -1) }));

  const addLine = () =>
    update((current) => ({
      ...current,
      containers: current.containers.map((container, index) =>
        index === current.containers.length - 1
          ? { ...container, cargo_lines: [...container.cargo_lines, blankCargoLine()] }
          : container
      ),
    }));

  const removeLine = () =>
    update((current) => ({
      ...current,
      containers: current.containers.map((container, index) =>
        index === current.containers.length - 1
          ? { ...container, cargo_lines: container.cargo_lines.slice(0, -1) }
          : container
      ),
    }));

  const lastLine = lastContainer?.cargo_lines[lastContainer.cargo_lines.length - 1];
  const canRemoveContainer = form.containers.length > 1;
  const canRemoveLine = lastContainer !== undefined && lastContainer.cargo_lines.length > 1;

  /** The messages for one row, under it — the grid's cells are too narrow to hold them. */
  const rowErrors = (keys: string[]): string[] =>
    Object.entries(errors)
      .filter(([path]) => keys.some((key) => path.startsWith(`${key}.`)))
      .map(([, message]) => message);

  return (
    <>
      <div className="shipp__seg" role="group" aria-label="Load type">
        {LOADS.map((load) => (
          <button
            key={load.value}
            type="button"
            className={cn("shipp__seg-b", { "is-on": form.load_type === load.value })}
            aria-pressed={form.load_type === load.value}
            onClick={() => switchTo(load.value)}
          >
            {load.label}
            <em>{load.hint}</em>
          </button>
        ))}
      </div>

      {errors[""] !== undefined && <p className="pc-note pc-note--bad shipp__err-top">{errors[""]}</p>}

      <div className="shipp__sec">
        <div className="shipp__sec-h">Shipment identity</div>
        <div className="shipp__grid3">
          {/* The API takes no carrier on this route, so the line is shown, not picked. */}
          <Field label="Shipping Line">
            <input type="text" className="shipp__in" value={carrierLabel(shipment)} readOnly />
          </Field>
          <Field label="Booking number" error={errors.booking_number}>
            <input
              type="text"
              className="shipp__in"
              value={form.booking_number}
              placeholder="e.g. QT-BK-482913"
              autoComplete="off"
              onChange={(event) => setField("booking_number", event.target.value)}
            />
          </Field>
          <Field label={bill.label} error={errors.master_bl}>
            <input
              type="text"
              className="shipp__in"
              value={form.master_bl}
              placeholder={bill.placeholder}
              autoComplete="off"
              onChange={(event) => setField("master_bl", event.target.value)}
            />
          </Field>
          <Field label="House Bill of Lading" error={errors.house_bl}>
            <input
              type="text"
              className="shipp__in"
              value={form.house_bl}
              placeholder="e.g. QTHBL-0042187"
              autoComplete="off"
              onChange={(event) => setField("house_bl", event.target.value)}
            />
          </Field>
        </div>
      </div>

      <div className="shipp__sec">
        <div className="shipp__sec-h">Product details</div>
        <div className="shipp__grid2">
          <Field label="Item name" error={errors.item_name}>
            <input
              type="text"
              className="shipp__in"
              value={form.item_name}
              placeholder="e.g. Lithium battery packs"
              autoComplete="off"
              onChange={(event) => setField("item_name", event.target.value)}
            />
          </Field>
          <Field label="Short description" error={errors.item_description}>
            <input
              type="text"
              className="shipp__in"
              value={form.item_description}
              placeholder="e.g. 12 SKU · 18 pallets"
              autoComplete="off"
              onChange={(event) => setField("item_description", event.target.value)}
            />
          </Field>
        </div>
      </div>

      <div className="shipp__sec">
        <div className="shipp__sec-h">
          <span>{lcl ? "Container & cargo" : "Containers"}</span>
          <em className="shipp__cnt">{gridCount(form)}</em>
        </div>

        <div className="shipp__box">
          <div className={cn("shipp__ct-hd", { "shipp__ct-hd--lcl": lcl })}>
            <span>Container number</span>
            {!lcl && (
              <>
                <span>DG class</span>
                <span>GW, mt</span>
                <span>Container type</span>
              </>
            )}
            <span>Details</span>
          </div>

          {form.containers.map((container) => {
            const messages = rowErrors([container.key, ...container.cargo_lines.map((line) => line.key)]);
            const documents = container.id === null ? 0 : (documentCounts[container.id] ?? 0);

            return (
              <div key={container.key} className={cn("shipp__ct", { "shipp__ct--lcl": lcl })}>
                <input
                  type="text"
                  className={cn("shipp__in shipp__ct-num", { "is-bad": errors[`${container.key}.number`] })}
                  value={container.number}
                  placeholder="Container number"
                  autoComplete="off"
                  aria-label="Container number"
                  onChange={(event) => setContainer(container.key, { number: event.target.value })}
                />

                {!lcl && (
                  <>
                    <span className={cn("shipp__dgw", { "shipp__dgw--cg": hasCargoGroup(container.dg_class) })}>
                      <Select
                        className="pcsel--dg"
                        ariaLabel="DG class"
                        value={container.dg_class}
                        options={DG_SELECT}
                        onChange={(dg_class) =>
                          setContainer(container.key, {
                            dg_class,
                            // Class 1 opens on group A, as the design's picker does.
                            cargo_group: hasCargoGroup(dg_class) ? container.cargo_group || CARGO_GROUPS[0] : "",
                          })
                        }
                      />
                      {hasCargoGroup(container.dg_class) && (
                        <Select
                          className="pcsel--cg shipp__cg"
                          ariaLabel="Compatibility group"
                          value={container.cargo_group}
                          options={options(CARGO_GROUPS)}
                          onChange={(cargo_group) => setContainer(container.key, { cargo_group })}
                        />
                      )}
                    </span>
                    <input
                      type="text"
                      className={cn("shipp__in shipp__ct-gw", {
                        "is-bad": errors[`${container.key}.gross_weight_mt`],
                      })}
                      value={container.gross_weight_mt}
                      placeholder="0.000"
                      inputMode="decimal"
                      autoComplete="off"
                      aria-label="Gross weight in tonnes"
                      onChange={(event) =>
                        setContainer(container.key, { gross_weight_mt: sanitizeDecimal(event.target.value) })
                      }
                    />
                    <Select
                      className="pcsel--ct"
                      ariaLabel="Container type"
                      value={container.container_type}
                      placeholder="Select type"
                      options={options(withCurrent(CONTAINER_TYPES, container.container_type))}
                      onChange={(container_type) => setContainer(container.key, { container_type })}
                    />
                  </>
                )}

                <span className="shipp__cta">
                  <button
                    type="button"
                    className="shipp__link"
                    title="Notes"
                    aria-label="Notes"
                    onClick={() => onOpenNote(container.key)}
                  >
                    <NotesIcon />
                  </button>
                  {/* Files hang off a saved container: a new row has no id to attach them to yet. */}
                  <button
                    type="button"
                    className="shipp__link"
                    title={container.id === null ? "Save the container to attach files" : "Attach files"}
                    aria-label="Attach files"
                    disabled={container.id === null}
                    onClick={() => container.id !== null && onOpenDocuments(container.id)}
                  >
                    <AttachIcon />
                    {documents > 0 && <em>{documents}</em>}
                  </button>
                </span>

                {lcl && (
                  <>
                    <span className="shipp__ct2 shipp__ct2--hd">
                      <span>Cargo name</span>
                      <span>Package Type / Quantity</span>
                      <span>Gross Weight</span>
                      <span>Volume</span>
                    </span>
                    {container.cargo_lines.map((line) => (
                      <span key={line.key} className="shipp__ct2">
                        <input
                          type="text"
                          className="shipp__in"
                          value={line.name}
                          placeholder="Cargo name"
                          autoComplete="off"
                          aria-label="Cargo name"
                          onChange={(event) => setLine(container.key, line.key, { name: event.target.value })}
                        />
                        <Select
                          className="pcsel--pkg"
                          ariaLabel="Package type"
                          value={line.package_type}
                          options={options(withCurrent(PACKAGE_TYPES, line.package_type))}
                          onChange={(package_type) => setLine(container.key, line.key, { package_type })}
                        />
                        <input
                          type="text"
                          className="shipp__in shipp__ct-num2"
                          value={line.package_quantity}
                          placeholder="0"
                          inputMode="numeric"
                          autoComplete="off"
                          aria-label="Package quantity"
                          onChange={(event) =>
                            setLine(container.key, line.key, { package_quantity: sanitizeInteger(event.target.value) })
                          }
                        />
                        <span className="shipp__ctcombo">
                          <input
                            type="text"
                            className="shipp__in shipp__ct-num2"
                            value={line.gross_weight}
                            placeholder="0"
                            inputMode="decimal"
                            autoComplete="off"
                            aria-label="Gross weight"
                            onChange={(event) =>
                              setLine(container.key, line.key, { gross_weight: sanitizeDecimal(event.target.value) })
                            }
                          />
                          <Select
                            className="pcsel--unit"
                            ariaLabel="Weight unit"
                            value={line.weight_unit}
                            options={options(WEIGHT_UNITS)}
                            onChange={(unit) =>
                              setLine(container.key, line.key, { weight_unit: unit as CargoLineForm["weight_unit"] })
                            }
                          />
                        </span>
                        <span className="shipp__ctcombo">
                          <input
                            type="text"
                            className="shipp__in shipp__ct-num2"
                            value={line.volume}
                            placeholder="0.00"
                            inputMode="decimal"
                            autoComplete="off"
                            aria-label="Volume"
                            onChange={(event) =>
                              setLine(container.key, line.key, { volume: sanitizeDecimal(event.target.value) })
                            }
                          />
                          <Select
                            className="pcsel--unit"
                            ariaLabel="Volume unit"
                            value={line.volume_unit}
                            options={options(VOLUME_UNITS)}
                            onChange={(unit) =>
                              setLine(container.key, line.key, { volume_unit: unit as CargoLineForm["volume_unit"] })
                            }
                          />
                        </span>
                      </span>
                    ))}
                  </>
                )}

                {messages.length > 0 && <em className="shipp__err shipp__ct-err">{messages.join(" ")}</em>}
              </div>
            );
          })}

          <div className="shipp__ctrow">
            {lcl ? (
              <>
                <button type="button" className="shipp__add" aria-label="Add cargo line" onClick={addLine}>
                  <AddRowIcon />
                </button>
                {canRemoveLine && (
                  <button
                    type="button"
                    className="shipp__add shipp__add--del"
                    aria-label="Remove last cargo line"
                    onClick={() =>
                      lastLine !== undefined && lineHasData(lastLine) ? setConfirm("line") : removeLine()
                    }
                  >
                    <RemoveRowIcon />
                  </button>
                )}
              </>
            ) : (
              <>
                <button type="button" className="shipp__add" aria-label="Add container" onClick={addContainer}>
                  <AddRowIcon />
                </button>
                {canRemoveContainer && (
                  <button
                    type="button"
                    className="shipp__add shipp__add--del"
                    aria-label="Remove last container"
                    onClick={() =>
                      lastContainer !== undefined && containerHasData(lastContainer)
                        ? setConfirm("container")
                        : removeContainer()
                    }
                  >
                    <RemoveRowIcon />
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {confirm === "container" && (
        <Modal
          title="Delete this container?"
          subtitle="The last container already has data entered. It will be removed together with its number, DG class, gross weight and container type, and any documents attached to it."
          onClose={() => setConfirm(null)}
          actions={
            <>
              <button type="button" className="pcbtn" onClick={() => setConfirm(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="pcbtn pcbtn--main"
                onClick={() => {
                  setConfirm(null);
                  removeContainer();
                }}
              >
                Yes, delete
              </button>
            </>
          }
        />
      )}

      {confirm === "line" && (
        <Modal
          title="Delete this cargo line?"
          subtitle="The last cargo line already has data entered. It will be removed together with its package type, quantity, weight and volume."
          onClose={() => setConfirm(null)}
          actions={
            <>
              <button type="button" className="pcbtn" onClick={() => setConfirm(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="pcbtn pcbtn--main"
                onClick={() => {
                  setConfirm(null);
                  removeLine();
                }}
              >
                Yes, delete
              </button>
            </>
          }
        />
      )}

      {confirm === "lcl" && (
        <Modal
          title="Switch to LCL?"
          onClose={() => setConfirm(null)}
          actions={
            <>
              <button type="button" className="pcbtn" onClick={() => setConfirm(null)}>
                No, keep FCL
              </button>
              <button
                type="button"
                className="pcbtn pcbtn--main"
                onClick={() => {
                  setConfirm(null);
                  commitLcl();
                }}
              >
                Yes, switch to LCL
              </button>
            </>
          }
        >
          <p className="pcmodal__t">
            This shipment has <b>{form.containers.length}</b> containers. LCL keeps only one line, so container numbers,
            types, weights and documents attached to the other containers will be lost once you save the shipment as
            LCL.
          </p>
        </Modal>
      )}
    </>
  );
};
